import { API } from "apis";
import AppTooltip from "components/common/AppTooltip";
import Chip from "components/common/Chip";
import FileInput from "components/common/input/FileInput";
import PageTitle from "components/common/PageTitle";
import CategoryCorpForms from "components/corp/profile/CategoryCorpForms";
import CorpProfileEditConfirm from "components/corp/profile/CorpProfileEditConfirm";
import TagDialog from "components/dialog/TagDialog";
import { TOOLTIP_DATA } from "enums/tooltip_data";
import { usePrompt } from "hooks/usePrompt";
import { CategoryModel, ContextModel, CorpPRModel } from "models";
import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Utils } from "utils";

const CorpProfileEditPage: React.FC = () => {
  const navigate = useNavigate();

  const ctx = useOutletContext<ContextModel>();
  const didEffect = React.useRef(false);

  const [categories, setCategories] = React.useState<CategoryModel[]>([]);
  const [data, setData] = React.useState<CorpPRModel>();
  const [tagEditData, setTagEditData] = React.useState<{ tag_name: string }[]>();
  const [formIsDirty, setFormIsDirty] = React.useState(true);
  const [confirm, setConfirm] = React.useState(false);

  const onNext = async () => {
    if (!data) {
      return;
    }

    setFormIsDirty(false);
    ctx.loading.setState(true);
    try {
      const response = await API.CorpProfile.update(data);
      console.log(response);
      ctx.msgDialog.setState({
        message: "会社PR情報の登録が完了しました。",
        callback: () => navigate("/mypage/"),
      });
    } catch (err) {
      Utils.errorAction(ctx, err);
    }
    ctx.loading.setState(false);
  };

  const onBeforeUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    // ここに設定する文字列は表示には影響しない？
    e.returnValue = "check";
  };

  usePrompt("入力中の内容は破棄されます。よろしいですか？", formIsDirty);

  const onOpenTagEditDialog = () => {
    if (!data) {
      return;
    }
    const tmp = data.corp_tag_list.map((v) => {
      return { tag_name: v.corp_tag_name };
    });
    setTagEditData(tmp);
  };
  const onCloseTagEditDialog = (tagData: { tag_name: string }[]) => {
    console.log(tagData);
    setTagEditData(undefined);
    if (!data) {
      return;
    }
    data.corp_tag_list = tagData.map((v) => {
      return {
        corp_tag_name: v.tag_name,
      };
    });
  };

  const onToConfirm = async () => {
    if (!data) {
      return;
    }

    ctx.loading.setState(true);
    try {
      //
      data.corp_category_list = data.corp_category_list.filter((v) => v.corp_large_category_id);
      // data.corp_category_list.forEach((v) => {
      //   if (v.corp_medium_category_id === undefined) {
      //     v.corp_medium_category_id = "";
      //   }
      // });
      if (data.corp_category_list.length === 0) {
        data.corp_category_list.push({ corp_large_category_id: "" });
      }

      const response = await API.CorpProfile.preCheck(data);
      console.log(response);
      setConfirm(true);
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });
    } catch (err) {
      Utils.errorAction(ctx, err);
    }
    ctx.loading.setState(false);
  };

  React.useEffect(() => {
    if (didEffect.current) {
      return;
    }
    didEffect.current = true;
    const f = async () => {
      ctx.loading.setState(true);
      await ctx.auth.initialize();

      try {
        {
          const response = await API.CategoryCorp.list();
          console.log(response);
          setCategories(response.data);
        }
        const response = await API.CorpProfile.detail();
        console.log(response);
        setData(response.data);
      } catch (err) {
        Utils.errorAction(ctx, err);
      }
      ctx.loading.setState(false);
    };
    f();

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) {
    return <></>;
  }

  if (confirm) {
    return (
      <div className="w-full px-12">
        <Helmet>
          <title>会社PR設定</title>
        </Helmet>
        <CorpProfileEditConfirm data={data} category={categories} />
        <div className="flex justify-center gap-4 m-8">
          <button className="primary px-4 py-2" onClick={onNext}>
            登録する
          </button>
          <button
            className="secondary px-4 py-2"
            onClick={() => {
              setConfirm(false);
            }}
          >
            戻る
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-12">
      <Helmet>
        <title>会社PR設定</title>
      </Helmet>
      <div className="w-full flex flex-col gap-2">
        <PageTitle title="会社PR設定" className="py-2" />

        <div className="mt-8 border-b"></div>

        <div className="flex justify-between items-center">
          <div className="w-3/12 flex justify-between">
            会社キャッチコピー
            <AppTooltip tooltip={TOOLTIP_DATA.CORPS_PROFILE.catch_phrase} />
          </div>
          <div className="w-9/12">
            <input
              className="w-full"
              value={data.catch_phrase}
              onChange={(e) => {
                data.catch_phrase = e.target.value;
                setData({ ...data });
              }}
            />
          </div>
        </div>
        <div className="flex justify-between items-start">
          <div className="w-3/12 mt-1 flex justify-between">
            ロゴ画像
            <AppTooltip tooltip={TOOLTIP_DATA.CORPS_PROFILE.logo_image} />
          </div>
          <div className="w-9/12">
            <FileInput data={data.logo_image} preview ctx={ctx} name="ロゴ画像" maxSize={1} />
          </div>
        </div>
        <div className="flex justify-between items-start">
          <div className="w-3/12 mt-1 flex justify-between">
            メイン画像
            <AppTooltip tooltip={TOOLTIP_DATA.CORPS_PROFILE.corp_image} />
          </div>
          <div className="w-9/12">
            <FileInput data={data.corp_image} preview ctx={ctx} name="メイン画像" maxSize={1} />
          </div>
        </div>
        <div className="flex justify-between items-start">
          <div className="w-3/12 mt-2">
            会社カテゴリ <span className="ml-2 mr-auto text-red-500 font-bold">*</span>
          </div>
          <div className="w-9/12">
            <CategoryCorpForms category={categories} data={data.corp_category_list}/>
          </div>
        </div>
        <div className="flex justify-between items-center">
          <div className="w-3/12">タグ</div>
          <div className="w-9/12 flex gap-2">
            <div className="mb-4">
              <button className="secondary px-4 py-2" onClick={onOpenTagEditDialog}>
                タグを追加する
              </button>
              <div className="mt-2 flex flex-wrap gap-2">
                {data.corp_tag_list.map((v, i) => {
                  return <Chip key={i} text={v.corp_tag_name} color="#d1d5db" textColor="#000000" />;
                })}
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-between items-start">
          <div className="w-3/12 pt-2 flex justify-between">
            会社紹介
            <AppTooltip tooltip={TOOLTIP_DATA.CORPS_PROFILE.introduction} />
          </div>
          <div className="w-9/12 flex gap-2">
            <textarea
              className="w-full h-32"
              value={data.introduction}
              onChange={(e) => {
                data.introduction = e.target.value;
                setData({ ...data });
              }}
            />
          </div>
        </div>
        <div className="flex justify-between items-start">
          <div className="w-3/12 pt-2 flex justify-between">
            PR画像&nbsp;01
            <AppTooltip tooltip={TOOLTIP_DATA.CORPS_PROFILE.pr_note} />
          </div>
          <div className="w-9/12">
            <FileInput
              data={data.pr_image_1}
              preview
              onChange={() => {
                if (!Utils.hasFile(data.pr_image_1)) {
                  data.pr_image_text_1 = "";
                }
                setData({ ...data });
              }}
              ctx={ctx}
              name="PR画像01"
              maxSize={1}
            />
            <textarea
              className="w-full h-32 mt-2"
              placeholder="写真の説明文"
              value={data.pr_image_text_1}
              onChange={(e) => {
                data.pr_image_text_1 = e.target.value;
                setData({ ...data });
              }}
              disabled={!Utils.hasFile(data.pr_image_1)}
            />
          </div>
        </div>
        <div className="flex justify-between items-start">
          <div className="w-3/12 pt-2 flex justify-between">
            PR画像&nbsp;02
            <AppTooltip tooltip={TOOLTIP_DATA.CORPS_PROFILE.pr_note} />
          </div>
          <div className="w-9/12">
            <FileInput
              data={data.pr_image_2}
              preview
              onChange={() => {
                if (!Utils.hasFile(data.pr_image_2)) {
                  data.pr_image_text_2 = "";
                }
                setData({ ...data });
              }}
              ctx={ctx}
              name="PR画像02"
              maxSize={1}
            />
            <textarea
              className="w-full h-32 mt-2"
              placeholder="写真の説明文"
              value={data.pr_image_text_2}
              onChange={(e) => {
                data.pr_image_text_2 = e.target.value;
                setData({ ...data });
              }}
              disabled={!Utils.hasFile(data.pr_image_2)}
            />
          </div>
        </div>
        <div className="flex justify-between items-start">
          <div className="w-3/12 pt-2 flex justify-between">
            PR画像&nbsp;03
            <AppTooltip tooltip={TOOLTIP_DATA.CORPS_PROFILE.pr_note} />
          </div>
          <div className="w-9/12">
            <FileInput
              data={data.pr_image_3}
              preview
              onChange={() => {
                if (!Utils.hasFile(data.pr_image_3)) {
                  data.pr_image_text_3 = "";
                }
                setData({ ...data });
              }}
              ctx={ctx}
              name="PR画像03"
              maxSize={1}
            />
            <textarea
              className="w-full h-32 mt-2"
              placeholder="写真の説明文"
              value={data.pr_image_text_3}
              onChange={(e) => {
                data.pr_image_text_3 = e.target.value;
                setData({ ...data });
              }}
              disabled={!Utils.hasFile(data.pr_image_3)}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-center m-8">
        <button className="primary px-4 py-2" onClick={onToConfirm}>
          入力内容の確認
        </button>
      </div>
      <TagDialog flg={!!tagEditData} data={tagEditData} close={onCloseTagEditDialog} />
    </div>
  );
};
export default CorpProfileEditPage;
